import type { EndpointCombo, FeatureId, FeatureRouteMap } from "../../services/featureRoutes";
import { isAllowedLlmUpstreamUrl, isBlockedLlmUpstreamUrl } from "../../lib/llmAllowedHosts";

interface EncryptedBackup {
  version: 1 | 2;
  kdf: string;
  salt: string;
  iv: string;
  ciphertext: string;
}
interface MortalCombo {
  id: string;
  label: string;
  api_url: string;
  api_key: string;
  model: string;
  temperature?: number;
  api_format?: string;
  maxOutputTokens?: number;
  enabled?: boolean;
}
interface MortalPlaintext {
  version: 1 | 2;
  endpointCombos: MortalCombo[];
  featureRoutes: Record<string, string[]>;
  ragApiSecrets?: {
    embeddingApiKey?: string;
    customEmbeddingApiKey?: string;
    rerankApiKey?: string;
  };
  ragConnection?: {
    embeddingProvider?: "siliconflow-cn" | "siliconflow-com" | "custom";
    embeddingUrl?: string;
    customEmbeddingUrl?: string;
    embeddingModel?: string;
    rerankEnabled?: boolean;
  };
}
export interface MortalImportPreview {
  combos: EndpointCombo[];
  featureRoutes: FeatureRouteMap;
  keys: Record<string, string>;
  warnings: string[];
  ignored: string[];
}

const featureMap: Record<string, FeatureId[]> = {
  "core-chat": ["core-chat"],
  evolution: ["evolution"],
  "evolution-entry": ["evolution-entry"],
  "evolution-item": ["evolution-item"],
  "evolution-player": ["evolution-player"],
  "evolution-npc": ["evolution-npc"],
  "evolution-beast": ["evolution-monster"],
  "evolution-misc": ["evolution-misc"],
  "evolution-bio": ["evolution-bio"],
  "deep-summary": ["deep-summary"],
  "quick-chat": ["quick-chat", "quick-chat-group"],
  "rag-query": ["rag-query"],
  "rag-embedding": ["rag-embedding"],
  "narrative-memory": ["narrative-memory"],
  "narrative-memory-before-send": ["narrative-memory"],
  "narrative-memory-after-reply": ["narrative-memory"],
  "branch-options": ["branch-options"],
  "plot-evolution": ["plot-evolution"],
  "writing-directive": ["plot-evolution"],
  crafting: ["crafting"],
};

function bytes(value: string) {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function assertEncrypted(value: unknown): EncryptedBackup {
  const envelope = value && typeof value === "object" && "backup" in value
    ? (value as { backup?: unknown }).backup
    : value;
  if (!envelope || typeof envelope !== "object") throw new Error("Mortal API 路由备份格式无效");
  const backup = envelope as Record<string, unknown>;
  if ((backup.version !== 1 && backup.version !== 2) || backup.kdf !== "PBKDF2-SHA256-250000" || typeof backup.salt !== "string" || typeof backup.iv !== "string" || typeof backup.ciphertext !== "string")
    throw new Error("Mortal API 路由备份格式无效");
  return backup as unknown as EncryptedBackup;
}

export async function decryptMortalApiRoutes(value: unknown, password: string): Promise<MortalPlaintext> {
  const backup = assertEncrypted(value);
  try {
    const passwordKey = await crypto.subtle.importKey("raw", new TextEncoder().encode(password.normalize("NFKC")), "PBKDF2", false, ["deriveKey"]);
    const key = await crypto.subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt: bytes(backup.salt), iterations: 250_000 }, passwordKey, { name: "AES-GCM", length: 256 }, false, ["decrypt"]);
    const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv: bytes(backup.iv) }, key, bytes(backup.ciphertext));
    const parsed = JSON.parse(new TextDecoder().decode(decrypted)) as MortalPlaintext;
    if (!Array.isArray(parsed.endpointCombos) || !parsed.featureRoutes || typeof parsed.featureRoutes !== "object") throw new Error("shape");
    return parsed;
  } catch { throw new Error("备份密码不正确，或 Mortal API 路由备份已损坏"); }
}

export function adaptMortalApiRoutes(source: MortalPlaintext, preferencePayload?: unknown): MortalImportPreview {
  const warnings: string[] = [];
  const ignored = new Set<string>();
  const referenced = new Set(Object.values(source.featureRoutes).flat());
  const embeddingIds = new Set(source.featureRoutes["rag-embedding"] ?? []);
  const generationIds = new Set([...referenced].filter((id) => !embeddingIds.has(id) || Object.entries(source.featureRoutes).some(([feature, ids]) => feature !== "rag-embedding" && ids.includes(id))));
  const combos: EndpointCombo[] = [];
  const keys: Record<string, string> = {};
  let importedRagId: string | null = null;
  const idFor = (id: string, kind: "generation" | "embedding") => embeddingIds.has(id) && generationIds.has(id) ? `${id}:${kind}` : id;
  for (const combo of source.endpointCombos) {
    if (!combo?.id || !combo.api_url || !combo.model) { warnings.push("忽略了一个字段不完整的 Mortal Combo。"); continue; }
    if (isBlockedLlmUpstreamUrl(combo.api_url)) { warnings.push(`${combo.label || combo.id} 的 host 被 MH 安全策略禁止，未导入。`); continue; }
    const kinds: Array<"generation" | "embedding"> = [
      ...(generationIds.has(combo.id) || !embeddingIds.has(combo.id) ? ["generation" as const] : []),
      ...(embeddingIds.has(combo.id) ? ["embedding" as const] : []),
    ];
    for (const kind of kinds) {
      const id = idFor(combo.id, kind);
      const allowed = isAllowedLlmUpstreamUrl(combo.api_url);
      combos.push({ id, name: combo.label || combo.id, kind, enabled: combo.enabled !== false, baseUrl: combo.api_url, model: combo.model, useProxy: allowed, temperature: combo.temperature, apiStyle: combo.api_format === "responses" ? "responses" : "chat-completions", maxTokens: combo.maxOutputTokens, dimensions: kind === "embedding" ? 1024 : undefined });
      keys[id] = combo.api_key ?? "";
      if (!allowed) warnings.push(`${combo.label || combo.id} 的 host 不在代理白名单，已设为浏览器直连。`);
    }
  }
  const ragConnection = source.ragConnection;
  if (ragConnection) {
    const custom = ragConnection.embeddingProvider === "custom";
    const baseUrl = (custom ? ragConnection.customEmbeddingUrl || ragConnection.embeddingUrl : ragConnection.embeddingUrl)?.trim() ?? "";
    const model = ragConnection.embeddingModel?.trim() ?? "";
    if (baseUrl && model) {
      let id = "mortal-rag-embedding";
      while (combos.some((combo) => combo.id === id)) id = `${id}-imported`;
      if (isBlockedLlmUpstreamUrl(baseUrl)) {
        warnings.push("Mortal 知识库 Embedding 的 host 被 MH 安全策略禁止，未导入。");
      } else {
        const allowed = isAllowedLlmUpstreamUrl(baseUrl);
        combos.push({ id, name: custom ? "Mortal 自定义 Embedding" : "Mortal 知识库 Embedding", kind: "embedding", enabled: true, baseUrl, model, useProxy: allowed, dimensions: 1024, timeoutMs: 60_000, retries: 0 });
        keys[id] = (custom ? source.ragApiSecrets?.customEmbeddingApiKey : source.ragApiSecrets?.embeddingApiKey) ?? "";
        importedRagId = id;
        if (!allowed) warnings.push("Mortal 知识库 Embedding 的 host 不在代理白名单，已设为浏览器直连。");
      }
    }
    if (ragConnection.rerankEnabled) ignored.add("知识库：Rerank 配置");
  }
  const importedIds = new Set(combos.map((combo) => combo.id));
  const featureRoutes: FeatureRouteMap = {};
  for (const [mortalFeature, routeIds] of Object.entries(source.featureRoutes)) {
    const targets = featureMap[mortalFeature];
    if (!targets) { ignored.add(`功能路由：${mortalFeature}`); continue; }
    for (const target of targets) {
      const kind = target === "rag-embedding" ? "embedding" : "generation";
      const mapped = routeIds.map((id) => idFor(id, kind)).filter((id) => importedIds.has(id));
      if (mapped.length && (featureRoutes[target] === undefined || mortalFeature === target)) featureRoutes[target] = mapped;
    }
  }
  if (importedRagId) featureRoutes["rag-embedding"] = [importedRagId];
  const preferences = preferencePayload && typeof preferencePayload === "object" && "backup" in preferencePayload
    ? (preferencePayload as { backup?: { rows?: Array<{ key?: string }> } }).backup
    : undefined;
  for (const row of preferences?.rows ?? []) if (row.key) ignored.add(`偏好项：${row.key}`);
  const chunked = preferences && "rowChunks" in preferences
    ? (preferences as { rowChunks?: Array<{ key?: string }> }).rowChunks
    : undefined;
  for (const row of chunked ?? []) if (row.key) ignored.add(`偏好项：${row.key}`);
  return { combos, featureRoutes, keys, warnings: [...new Set(warnings)], ignored: [...ignored].sort() };
}
